/**
 * Shared Team Presence logic (page + dashboard strip use the same numbers).
 */

export const TIME_ZONE = "Africa/Cairo";
export const LATE_AFTER_DEFAULT = "12:00";
export const LATE_AFTER_KEY = "presence:lateAfter:v2";
export const DAY_TARGET_MIN = 8 * 60;
export const WEEK_TARGET_MIN = 32 * 60;
/** Records worked under this are ignored for check-in averages and lateness. */
export const TINY_RECORD_MIN = 15;

export const readLateAfter = () => {
  try {
    const v = localStorage.getItem(LATE_AFTER_KEY);
    return /^\d{2}:\d{2}$/.test(v || "") ? v : LATE_AFTER_DEFAULT;
  } catch {
    return LATE_AFTER_DEFAULT;
  }
};

/** "6h 23m" / "45m" / "2h" → minutes (CSV cells may carry \r / spaces). */
export const parseDuration = (text) => {
  const s = String(text ?? "").trim();
  if (!s) return 0;
  const h = /(\d+)\s*h/.exec(s);
  const m = /(\d+)\s*m/.exec(s);
  if (!h && !m && /^\d+$/.test(s)) return Number(s);
  return (h ? Number(h[1]) * 60 : 0) + (m ? Number(m[1]) : 0);
};

export const formatMinutes = (min) => {
  const m = Math.max(0, Math.round(min || 0));
  const h = Math.floor(m / 60);
  return h ? `${h}h ${m % 60}m` : `${m}m`;
};

const partsIn = (date) => {
  const p = Object.fromEntries(
    new Intl.DateTimeFormat("en-US", { timeZone: TIME_ZONE, hour: "2-digit", minute: "2-digit", hourCycle: "h23" })
      .formatToParts(new Date(date))
      .map((x) => [x.type, x.value]),
  );
  return { h: Number(p.hour), m: Number(p.minute) };
};

/** Minutes since local (Cairo) midnight. */
export const minuteOfDay = (date) => {
  const { h, m } = partsIn(date);
  return h * 60 + m;
};

export const formatTime = (date) =>
  date
    ? new Intl.DateTimeFormat("en-US", { timeZone: TIME_ZONE, hour: "2-digit", minute: "2-digit", hour12: true }).format(new Date(date))
    : "—";

export const formatClock = (minutes) => {
  const h = Math.floor(minutes / 60) % 24;
  const m = Math.round(minutes % 60);
  const d = new Date(Date.UTC(2000, 0, 1, h, m));
  return new Intl.DateTimeFormat("en-US", { timeZone: "UTC", hour: "2-digit", minute: "2-digit", hour12: true }).format(d);
};

export const todayIn = (d = new Date()) =>
  new Intl.DateTimeFormat("en-CA", { timeZone: TIME_ZONE, year: "numeric", month: "2-digit", day: "2-digit" }).format(d);

/** Sunday → today (Cairo) work week, as YYYY-MM-DD strings. */
export const weekRange = (today = todayIn()) => {
  const d = new Date(`${today}T12:00:00Z`);
  const start = new Date(d);
  start.setUTCDate(d.getUTCDate() - d.getUTCDay());
  return { from: start.toISOString().slice(0, 10), to: today };
};

/** Sunday → Saturday week containing `date`, uncapped (for labels). */
export const weekBounds = (date) => {
  const d = new Date(`${date}T12:00:00Z`);
  const start = new Date(d);
  start.setUTCDate(d.getUTCDate() - d.getUTCDay());
  const end = new Date(start);
  end.setUTCDate(start.getUTCDate() + 6);
  return { from: start.toISOString().slice(0, 10), to: end.toISOString().slice(0, 10) };
};

/** Sunday → Saturday week containing `date`, capped at today (for fetching). */
export const weekOf = (date, today = todayIn()) => {
  const d = new Date(`${date}T12:00:00Z`);
  const start = new Date(d);
  start.setUTCDate(d.getUTCDate() - d.getUTCDay());
  const end = new Date(start);
  end.setUTCDate(start.getUTCDate() + 6);
  const to = end.toISOString().slice(0, 10);
  return { from: start.toISOString().slice(0, 10), to: to > today ? today : to };
};

/** YYYY-MM-DD shifted by `days`. */
export const shiftDate = (date, days) => {
  const d = new Date(`${date}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};

/** "Oct 14" in the report's locale. */
export const fmtShort = (date) =>
  new Date(`${date}T12:00:00Z`).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });

/** Day-navigator label: "Today · Oct 14" / "Yesterday · Oct 13" / "Sun, Oct 4". */
export const fmtDayLabel = (date, today = todayIn()) => {
  if (date === today) return `Today · ${fmtShort(date)}`;
  if (date === shiftDate(today, -1)) return `Yesterday · ${fmtShort(date)}`;
  const wd = new Date(`${date}T12:00:00Z`).toLocaleDateString("en-US", { weekday: "short", timeZone: "UTC" });
  return `${wd}, ${fmtShort(date)}`;
};

/**
 * Week-navigator label: "This week · Oct 11 – 17", or "Oct 4 – 10".
 * The month is repeated on both sides when the week spans two months.
 */
export const fmtWeekLabel = ({ from, to }, today = todayIn()) => {
  const month = (d) => new Date(`${d}T12:00:00Z`).toLocaleDateString("en-US", { month: "short", timeZone: "UTC" });
  const day = (d) => new Date(`${d}T12:00:00Z`).toLocaleDateString("en-US", { day: "numeric", timeZone: "UTC" });
  const span = month(from) === month(to) ? `${fmtShort(from)} \u2013 ${day(to)}` : `${fmtShort(from)} \u2013 ${fmtShort(to)}`;
  return weekOf(today, today).from === from ? `This week \u00b7 ${span}` : span;
};

export const lateAfterMinutes = (lateAfter = LATE_AFTER_DEFAULT) => {
  const [h, m] = lateAfter.split(":").map(Number);
  return h * 60 + m;
};

/** Late = first check-in after the threshold (and after 05:00, so overnight sessions don't count). */
export const lateBy = (row, lateAfter) => {
  if (!row || row.workedMinutes < TINY_RECORD_MIN) return 0;
  const m = minuteOfDay(row.firstCheckIn);
  const limit = lateAfterMinutes(lateAfter);
  return m > limit && m >= 5 * 60 ? m - limit : 0;
};

/** Checked out when there is a check-out time or the status is DONE. */
export const isCheckedOut = (row) => Boolean(row.lastCheckOut) || row.status === "DONE";

/**
 * Status for a person today: live idle/working comes from the bot's live
 * endpoint; a row with a check-out (or DONE) is checked out.
 */
export const statusOf = (row, live) => {
  if (isCheckedOut(row)) return "out";
  const l = live?.get(row.userId);
  if (l?.status === "idle") return "idle";
  return "working";
};

export const STATUS_META = {
  working: { label: "Working", dot: "var(--green)", pill: "pill-ok" },
  idle: { label: "Idle", dot: "var(--amber)", pill: "pill-warn" },
  out: { label: "Checked out", dot: "var(--faint)", pill: "" },
};

/** Average first check-in (minute of day) over non-tiny rows, or null. */
export const avgCheckIn = (rows) => {
  const valid = rows.filter((r) => r.workedMinutes >= TINY_RECORD_MIN && minuteOfDay(r.firstCheckIn) >= 5 * 60);
  if (!valid.length) return null;
  return valid.reduce((s, r) => s + minuteOfDay(r.firstCheckIn), 0) / valid.length;
};

/** Today's KPIs (dashboard strip uses the same). */
export const todaySummary = (rows, live, lateAfter) => {
  const status = rows.map((r) => statusOf(r, live));
  const worked = rows.reduce((s, r) => s + r.workedMinutes, 0);
  const idle = rows.reduce((s, r) => s + r.idleMinutes, 0);
  return {
    working: status.filter((s) => s === "working").length,
    idle: status.filter((s) => s === "idle").length,
    out: status.filter((s) => s === "out").length,
    total: rows.length,
    avgCheckIn: avgCheckIn(rows),
    late: rows.filter((r) => lateBy(r, lateAfter) > 0).length,
    workedMinutes: worked,
    idleMinutes: idle,
    focusPct: worked + idle ? Math.round((worked / (worked + idle)) * 100) : 0,
  };
};

/** Aggregate rows per person (week view). */
export const perPerson = (rows) => {
  const map = new Map();
  for (const r of rows) {
    const p = map.get(r.userId) || {
      userId: r.userId,
      displayName: r.displayName,
      username: r.username,
      workedMinutes: 0,
      idleMinutes: 0,
      days: [],
    };
    p.displayName = r.displayName;
    p.workedMinutes += r.workedMinutes;
    p.idleMinutes += r.idleMinutes;
    p.days.push(r);
    map.set(r.userId, p);
  }
  return [...map.values()];
};

/** Linked employees' photos by Discord userId, filled from presence responses. */
const photoByUserId = new Map();
export const registerProfiles = (profiles) => {
  for (const [id, p] of Object.entries(profiles || {})) photoByUserId.set(id, p?.photo || null);
};
export const photoOf = (userId) => photoByUserId.get(String(userId)) || null;

/** Stable per-name hue (avatars + legend dots). */
export const hueOf = (name = "") => [...String(name)].reduce((h, c) => (h * 31 + c.charCodeAt(0)) % 360, 17);
export const initialsOf = (name = "") =>
  String(name)
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("") || "?";
